<template>
  <div class="hope-cert-block">
    <!-- 희망이음 공동인증서 섹션 -->
    <div class="ltc-info-title" style="margin-top:24px;">
      <v-icon size="18" class="mr-1">mdi-account-key</v-icon>
      희망이음 공동인증서
      <span class="hope-cert-badge">개인 인증서</span>
      <button
        v-if="certInfo && !loading"
        class="edit-btn"
        @click="openModal()"
      >
        <v-icon size="14">mdi-pencil</v-icon>
        수정
      </button>
    </div>

    <div class="cert-section">
      <!-- 확인 중 -->
      <div v-if="loading" class="cert-loading">
        <v-progress-circular indeterminate size="20" width="2" />
        <span>희망이음 인증서 정보 확인 중...</span>
      </div>

      <!-- 등록됨 -->
      <div v-else-if="certInfo" class="cert-registered">
        <table class="ltc-info-table">
          <tbody>
            <tr>
              <th>발급자</th>
              <td>{{ certInfo.chc_cert_name || '-' }}</td>
              <th>만료일</th>
              <td>
                <span :class="{ 'cert-date-alert': isExpired || isExpiringSoon }">
                  {{ formatDate(certInfo.chc_expire_date) }}
                  <span v-if="isExpired" class="expire-warning">만료되었습니다</span>
                  <span v-else-if="isExpiringSoon" class="expire-warning">(만료 임박)</span>
                </span>
              </td>
            </tr>
            <tr>
              <th>희망이음 아이디</th>
              <td>{{ certInfo.chc_hope_id_masked || '-' }}</td>
              <th>마지막 사용</th>
              <td>{{ certInfo.chc_last_used ? formatDateTime(certInfo.chc_last_used) : '사용 기록 없음' }}</td>
            </tr>
            <tr>
              <th>등록일</th>
              <td colspan="3">{{ formatDateTime(certInfo.chc_wdate) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 미등록 -->
      <div v-else class="cert-not-registered">
        <div class="cert-empty-icon">
          <v-icon size="40" color="grey-lighten-1">mdi-account-key-outline</v-icon>
        </div>
        <p class="cert-empty-text">등록된 희망이음 공동인증서가 없습니다</p>
        <p class="cert-empty-hint">개인 공동인증서와 희망이음 아이디/비밀번호를 등록하면 희망이음 자동화 기능을 이용할 수 있습니다</p>
        <button class="cert-upload-btn" @click="openModal()">
          <v-icon size="16">mdi-upload</v-icon>
          희망이음 인증서 등록
        </button>
      </div>
    </div>

    <!-- 업로드/수정 모달 -->
    <v-dialog v-model="showModal" max-width="600" persistent>
      <div class="cert-upload-modal">
        <div class="modal-header modal-header--dark">
          <h3>{{ certInfo ? '희망이음 공동인증서 관리' : '희망이음 공동인증서 등록' }}</h3>
          <button class="close-btn" @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="loadingCredentials" class="cert-loading">
            <v-progress-circular indeterminate size="20" width="2" />
            <span>희망이음 로그인정보 불러오는 중...</span>
          </div>

          <template v-else>
          <!-- 기존 인증서가 있을 때: 모드 탭 -->
          <div v-if="certInfo" class="cert-mode-tabs">
            <button :class="['mode-tab', { active: editMode === 'password' }]" @click="editMode = 'password'">
              <v-icon size="16" class="mr-1">mdi-key</v-icon>
              비밀번호 변경
            </button>
            <button :class="['mode-tab', { active: editMode === 'credentials' }]" @click="editMode = 'credentials'">
              <v-icon size="16" class="mr-1">mdi-account</v-icon>
              아이디/비번 변경
            </button>
            <button :class="['mode-tab', { active: editMode === 'full' }]" @click="editMode = 'full'">
              <v-icon size="16" class="mr-1">mdi-certificate</v-icon>
              인증서 전체 변경
            </button>
          </div>

          <!-- 비밀번호만 변경 -->
          <div v-if="certInfo && editMode === 'password'" class="password-only-mode">
            <div class="current-cert-info">
              <div class="info-label">현재 등록된 인증서</div>
              <div class="info-value">{{ certInfo.chc_cert_name || '알 수 없음' }}</div>
              <div class="info-sub">만료일: {{ formatDate(certInfo.chc_expire_date) }}</div>
            </div>
            <div class="upload-field">
              <label>새 인증서 비밀번호 <span class="required">*</span></label>
              <div class="password-input-wrap">
                <input
                  v-model="newPassword"
                  :type="showPw ? 'text' : 'password'"
                  class="native-input full-width"
                  placeholder="새 인증서 비밀번호를 입력하세요"
                />
                <button type="button" class="toggle-password-btn" @click="showPw = !showPw">
                  <v-icon size="18">{{ showPw ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                </button>
              </div>
            </div>
          </div>

          <!-- 아이디/비번만 변경 -->
          <div v-if="certInfo && editMode === 'credentials'" class="password-only-mode">
            <div class="upload-field">
              <label>희망이음 아이디 <span class="required">*</span></label>
              <input
                v-model="hopeId"
                type="text"
                class="native-input full-width"
                placeholder="희망이음 로그인 아이디"
                name="hope_ssis_login_id"
                autocomplete="off"
                autocapitalize="off"
                spellcheck="false"
              />
            </div>
            <div class="upload-field">
              <label>희망이음 비밀번호 <span class="required">*</span></label>
              <div class="password-input-wrap">
                <input
                  v-model="hopePw"
                  :type="showHopePw ? 'text' : 'password'"
                  class="native-input full-width"
                  placeholder="희망이음 로그인 비밀번호"
                  name="hope_ssis_login_pw"
                  autocomplete="new-password"
                />
                <button type="button" class="toggle-password-btn" @click="showHopePw = !showHopePw">
                  <v-icon size="18">{{ showHopePw ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                </button>
              </div>
            </div>
          </div>

          <!-- 전체 변경 / 신규 등록 -->
          <template v-if="!certInfo || editMode === 'full'">
            <div class="cert-guide npki-guide">
              <div class="guide-header">
                <v-icon size="20" color="primary">mdi-information</v-icon>
                <strong>인증서 폴더 한 번에 올리기</strong>
              </div>
              <div class="guide-steps">
                <div class="guide-step">
                  <div class="step-number">1</div>
                  <div class="step-content">
                    <span class="step-title">아래 「인증서 폴더 선택」 버튼 클릭</span>
                    <span class="step-desc">폴더를 고르는 창이 열립니다.</span>
                  </div>
                </div>
                <div class="guide-step">
                  <div class="step-number">2</div>
                  <div class="step-content">
                    <span class="step-title">열린 창 주소창에 경로 붙여넣기</span>
                    <span class="step-desc">경로를 복사한 뒤 창 위쪽 주소창에 붙여넣고 Enter</span>
                    <div class="path-box">
                      <code>%USERPROFILE%\AppData\LocalLow\NPKI</code>
                      <button type="button" class="copy-path-btn" @click="copyNpkiPath">
                        <v-icon size="14">mdi-content-copy</v-icon>
                      </button>
                    </div>
                  </div>
                </div>
                <div class="guide-step">
                  <div class="step-number">3</div>
                  <div class="step-content">
                    <span class="step-title">내 인증서 폴더까지 이동</span>
                    <span class="step-desc">
                      인증기관 폴더(예: yessign, SignKorea) → <b>USER</b> →
                      본인 인증서 폴더(cn=… 형태)를 선택하고 「폴더 선택」 확인
                    </span>
                    <span class="step-desc step-desc--tip">
                      인증서가 여러 개면 USER를 통째로 고르지 말고, 사용할 인증서 폴더 하나만 선택하세요.
                    </span>
                  </div>
                </div>
                <div class="guide-step">
                  <div class="step-number">4</div>
                  <div class="step-content">
                    <span class="step-title">자동 인식 확인</span>
                    <span class="step-desc">
                      <span class="file-badge cert">signCert.der</span> /
                      <span class="file-badge key">signPri.key</span> 가 자동으로 채워집니다.
                      그 다음 비밀번호만 입력하면 됩니다.
                    </span>
                    <span class="step-desc step-desc--tip">
                      USB·다른 PC에 복사해 둔 인증서도 같은 방법으로 폴더만 선택하면 됩니다.
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="cert-change-notice">
              <v-icon size="16" color="primary">mdi-account-key</v-icon>
              <span>
                희망이음은 <b>개인(personal) 공동인증서</b>가 필요합니다.
                롱텀케어에 등록한 <b>법인 인증서와 다릅니다</b> — 희망이음용 개인 인증서를 별도로 등록해주세요.
              </span>
            </div>

            <div class="cert-notice hope-login-notice">
              <v-icon size="14" color="info">mdi-login</v-icon>
              <span>
                희망이음(ssis.go.kr) 로그인 <b>아이디·비밀번호</b>도 함께 입력해야
                마감요청 자동 업로드 등 자동화 기능이 동작합니다.
              </span>
            </div>

            <!-- 파일 선택 -->
            <div class="upload-field">
              <label>인증서 파일 <span class="required">*</span></label>
              <div class="cert-pick-actions">
                <button type="button" class="cert-folder-pick-btn" @click="triggerFolderInput">
                  <v-icon size="18" class="mr-1">mdi-folder-open</v-icon>
                  인증서 폴더 선택
                </button>
                <button type="button" class="cert-files-pick-btn" @click="triggerFileInput">
                  <v-icon size="16" class="mr-1">mdi-file-multiple-outline</v-icon>
                  파일 2개 직접 선택
                </button>
              </div>
              <input
                type="file"
                ref="folderInputRef"
                style="display: none"
                @change="onFolderChange"
              />
              <div
                class="file-drop-zone"
                :class="{ 'drag-over': isDragOver, 'has-files': signCertFile && signPriFile }"
                @dragover.prevent="isDragOver = true"
                @dragleave.prevent="isDragOver = false"
                @drop.prevent="onDrop"
              >
                <input
                  type="file"
                  ref="fileInputRef"
                  accept=".der,.key"
                  multiple
                  @change="onFilesChange"
                  style="display: none"
                />
                <div v-if="!signCertFile && !signPriFile" class="drop-zone-content">
                  <v-icon size="32" :color="isDragOver ? 'primary' : 'grey-lighten-1'">
                    {{ isDragOver ? 'mdi-file-download' : 'mdi-folder-upload-outline' }}
                  </v-icon>
                  <span class="drop-text">위 버튼으로 폴더를 선택하거나, 파일을 여기에 드래그하세요</span>
                  <span class="drop-hint">권장: 인증서 폴더 통째 선택 → der/key 자동 인식</span>
                </div>
                <div v-else class="drop-zone-files">
                  <div class="file-item" :class="{ 'has-file': signCertFile }">
                    <v-icon v-if="signCertFile" size="16" color="success">mdi-check-circle</v-icon>
                    <v-icon v-else size="16" color="grey-lighten-1">mdi-close-circle</v-icon>
                    <span class="file-badge cert">signCert.der</span>
                    <span class="file-status">{{ signCertFile ? signCertFile.name : '선택 안됨' }}</span>
                  </div>
                  <div class="file-item" :class="{ 'has-file': signPriFile }">
                    <v-icon v-if="signPriFile" size="16" color="success">mdi-check-circle</v-icon>
                    <v-icon v-else size="16" color="grey-lighten-1">mdi-close-circle</v-icon>
                    <span class="file-badge key">signPri.key</span>
                    <span class="file-status">{{ signPriFile ? signPriFile.name : '선택 안됨' }}</span>
                  </div>
                  <span v-if="pickedFolderHint" class="change-hint">폴더: {{ pickedFolderHint }}</span>
                  <span class="change-hint">위에서 다시 선택하면 변경됩니다</span>
                </div>
              </div>
            </div>

            <div class="upload-field">
              <label>인증서 비밀번호 <span class="required">*</span></label>
              <div class="password-input-wrap">
                <input
                  v-model="certPassword"
                  :type="showCertPw ? 'text' : 'password'"
                  class="native-input full-width"
                  placeholder="인증서 비밀번호"
                />
                <button type="button" class="toggle-password-btn" @click="showCertPw = !showCertPw">
                  <v-icon size="18">{{ showCertPw ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                </button>
              </div>
              <div class="password-hint">
                <v-icon size="12" color="warning" class="mr-1">mdi-alert</v-icon>
                등록 시 비밀번호가 이 인증서와 일치하는지 자동으로 확인됩니다.
              </div>
            </div>

            <div class="upload-field">
              <label>희망이음 아이디 <span class="required">*</span></label>
              <input
                v-model="hopeId"
                type="text"
                class="native-input full-width"
                placeholder="희망이음 로그인 아이디"
                name="hope_ssis_login_id"
                autocomplete="off"
                autocapitalize="off"
                spellcheck="false"
              />
            </div>
            <div class="upload-field">
              <label>희망이음 비밀번호 <span class="required">*</span></label>
              <div class="password-input-wrap">
                <input
                  v-model="hopePw"
                  :type="showHopePw ? 'text' : 'password'"
                  class="native-input full-width"
                  placeholder="희망이음 로그인 비밀번호"
                  name="hope_ssis_login_pw"
                  autocomplete="new-password"
                />
                <button type="button" class="toggle-password-btn" @click="showHopePw = !showHopePw">
                  <v-icon size="18">{{ showHopePw ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                </button>
              </div>
            </div>

            <div class="cert-notice">
              <v-icon size="14" color="warning">mdi-shield-lock</v-icon>
              <span>인증서·아이디·비밀번호는 모두 AES-256으로 암호화되어 저장됩니다.</span>
            </div>
          </template>
          </template>
        </div>

        <div class="modal-footer">
          <button
            v-if="certInfo && editMode === 'full'"
            class="delete-btn"
            @click="showDeleteDialog = true"
            :disabled="saving"
          >
            <v-icon size="14" class="mr-1">mdi-delete</v-icon>
            삭제
          </button>
          <div class="footer-spacer"></div>
          <button class="cancel-btn" @click="closeModal" :disabled="saving">취소</button>

          <button
            v-if="certInfo && editMode === 'password'"
            class="save-btn"
            @click="changePassword"
            :disabled="!newPassword.trim() || saving"
          >
            <v-progress-circular v-if="saving" indeterminate size="14" width="2" class="mr-1" />
            <v-icon v-else size="14" class="mr-1">mdi-content-save</v-icon>
            {{ saving ? '저장 중...' : '비밀번호 변경' }}
          </button>

          <button
            v-else-if="certInfo && editMode === 'credentials'"
            class="save-btn"
            @click="changeCredentials"
            :disabled="!hopeId.trim() || !hopePw.trim() || saving"
          >
            <v-progress-circular v-if="saving" indeterminate size="14" width="2" class="mr-1" />
            <v-icon v-else size="14" class="mr-1">mdi-content-save</v-icon>
            {{ saving ? '저장 중...' : '아이디/비번 변경' }}
          </button>

          <button
            v-else
            class="save-btn"
            @click="upload"
            :disabled="!canUpload || saving"
          >
            <v-progress-circular v-if="saving" indeterminate size="14" width="2" class="mr-1" />
            <v-icon v-else size="14" class="mr-1">mdi-upload</v-icon>
            {{ saving ? '업로드 중...' : (certInfo ? '인증서 변경' : '등록') }}
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- 삭제 확인 -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <div class="delete-confirm-dialog">
        <button
          type="button"
          class="dialog-x-close"
          aria-label="닫기"
          :disabled="saving"
          @click="showDeleteDialog = false"
        >
          <v-icon size="18">mdi-close</v-icon>
        </button>
        <div class="dialog-icon">
          <v-icon size="48" color="error">mdi-alert-circle</v-icon>
        </div>
        <h3>희망이음 인증서 삭제</h3>
        <p>등록된 희망이음 공동인증서를 삭제하시겠습니까?<br/>삭제 후에는 복구할 수 없습니다.</p>
        <div class="dialog-actions">
          <button class="cancel-btn" @click="showDeleteDialog = false">취소</button>
          <button class="delete-btn" @click="deleteCert" :disabled="saving">
            <v-progress-circular v-if="saving" indeterminate size="14" width="2" class="mr-1" />
            삭제
          </button>
        </div>
      </div>
    </v-dialog>

    <v-snackbar v-model="snackbar" :timeout="2000" :color="snackbarColor" location="bottom">
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { hopeCertApi } from '@/services/api';
import { pickNpkiCertFiles, NPKI_PATH_HINT } from '@/utils/pickNpkiCertFiles';

const props = defineProps<{ mCode?: string | number | null }>();

const loading = ref(false);
const certInfo = ref<any>(null);
const showModal = ref(false);
const showDeleteDialog = ref(false);
const editMode = ref<'password' | 'credentials' | 'full'>('full');
const saving = ref(false);
const loadingCredentials = ref(false);

// 파일/입력 상태
const fileInputRef = ref<HTMLInputElement | null>(null);
const folderInputRef = ref<HTMLInputElement | null>(null);
const signCertFile = ref<File | null>(null);
const signPriFile = ref<File | null>(null);
const pickedFolderHint = ref('');
const isDragOver = ref(false);
const certPassword = ref('');
const newPassword = ref('');
const hopeId = ref('');
const hopePw = ref('');
const showPw = ref(false);
const showCertPw = ref(false);
const showHopePw = ref(false);

// 스낵바
const snackbar = ref(false);
const snackbarText = ref('');
const snackbarColor = ref('success');
const notify = (text: string, color = 'success') => {
  snackbarText.value = text;
  snackbarColor.value = color;
  snackbar.value = true;
};

const copyNpkiPath = async () => {
  try {
    await navigator.clipboard.writeText(NPKI_PATH_HINT);
    notify('경로가 복사되었습니다.');
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = NPKI_PATH_HINT;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    notify('경로가 복사되었습니다.');
  }
};

const canUpload = computed(() =>
  !!signCertFile.value && !!signPriFile.value &&
  !!certPassword.value.trim() && !!hopeId.value.trim() && !!hopePw.value.trim()
);

const certExpireDayDiff = (raw: unknown) => {
  if (!raw) return null;
  const expireDate = new Date(raw as string);
  if (Number.isNaN(expireDate.getTime())) return null;
  const expire = new Date(expireDate.getFullYear(), expireDate.getMonth(), expireDate.getDate());
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((expire.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24));
};

const isExpired = computed(() => {
  const diffDays = certExpireDayDiff(certInfo.value?.chc_expire_date);
  return diffDays !== null && diffDays < 0;
});

const isExpiringSoon = computed(() => {
  const diffDays = certExpireDayDiff(certInfo.value?.chc_expire_date);
  return diffDays !== null && diffDays <= 30 && diffDays >= 0;
});

const formatDate = (d: any) => {
  if (!d) return '-';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '-';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};
const formatDateTime = (d: any) => {
  if (!d) return '-';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '-';
  return `${formatDate(d)} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const loadInfo = async () => {
  if (!props.mCode) { certInfo.value = null; return; }
  loading.value = true;
  try {
    const res = await hopeCertApi.getInfo(props.mCode);
    certInfo.value = res.data?.data || null;
  } catch (error: any) {
    if (error.response?.status !== 404) {
      const msg = error.response?.data?.message || error.message || '알 수 없는 오류';
      notify(`희망이음 인증서 조회 실패: ${msg}`, 'error');
    }
    certInfo.value = null;
  } finally {
    loading.value = false;
  }
};

const resetInputs = () => {
  signCertFile.value = null;
  signPriFile.value = null;
  pickedFolderHint.value = '';
  certPassword.value = '';
  newPassword.value = '';
  hopeId.value = '';
  hopePw.value = '';
  showPw.value = false;
  showCertPw.value = false;
  showHopePw.value = false;
};

const loadHopeCredentials = async () => {
  if (!props.mCode || !certInfo.value?.chc_idx) return;
  loadingCredentials.value = true;
  try {
    const res = await hopeCertApi.getCredentials(props.mCode);
    const data = res.data?.data;
    if (data) {
      hopeId.value = data.hopeId || '';
      hopePw.value = data.hopePw || '';
    }
  } catch (error: any) {
    const msg = error.response?.data?.message || error.message || '알 수 없는 오류';
    notify(`희망이음 로그인정보 조회 실패: ${msg}`, 'error');
  } finally {
    loadingCredentials.value = false;
  }
};

const openModal = async () => {
  resetInputs();
  // 만료된 인증서는 전체 변경이 기본
  editMode.value = certInfo.value && !isExpired.value ? 'password' : 'full';
  showModal.value = true;
  if (certInfo.value) {
    await loadHopeCredentials();
  }
};
const closeModal = () => {
  showModal.value = false;
  resetInputs();
};

const enableDirectoryPicker = (el: HTMLInputElement | null) => {
  if (!el) return;
  (el as HTMLInputElement & { webkitdirectory?: boolean }).webkitdirectory = true;
  el.setAttribute('webkitdirectory', '');
  el.setAttribute('directory', '');
  el.multiple = true;
};

const triggerFolderInput = () => {
  enableDirectoryPicker(folderInputRef.value);
  folderInputRef.value?.click();
};

const triggerFileInput = () => fileInputRef.value?.click();

const assignFiles = (files: FileList | File[]) => {
  const picked = pickNpkiCertFiles(files);
  if (!picked.ok) {
    notify(picked.message, 'error');
    return;
  }
  signCertFile.value = picked.der;
  signPriFile.value = picked.key;
  pickedFolderHint.value = picked.pairDir;
};

const onFolderChange = (e: Event) => {
  const input = e.target as HTMLInputElement;
  if (input.files) assignFiles(input.files);
  input.value = '';
};

const onFilesChange = (e: Event) => {
  const input = e.target as HTMLInputElement;
  if (input.files) assignFiles(input.files);
  input.value = '';
};

const onDrop = (e: DragEvent) => {
  isDragOver.value = false;
  if (e.dataTransfer?.files) assignFiles(e.dataTransfer.files);
};

const upload = async () => {
  if (!props.mCode || !canUpload.value) return;
  saving.value = true;
  try {
    const formData = new FormData();
    formData.append('mCode', String(props.mCode));
    formData.append('signCert', signCertFile.value as File);
    formData.append('signPri', signPriFile.value as File);
    formData.append('password', certPassword.value);
    formData.append('hopeId', hopeId.value.trim());
    formData.append('hopePw', hopePw.value.trim());
    await hopeCertApi.upload(formData);
    notify('희망이음 인증서가 등록되었습니다.');
    closeModal();
    await loadInfo();
  } catch (error: any) {
    const msg = error.response?.data?.message || error.message || '알 수 없는 오류';
    const status = error.response?.status || '';
    notify(`희망이음 인증서 등록 실패: ${msg}${status ? ` (${status})` : ''}`, 'error');
  } finally {
    saving.value = false;
  }
};

const changePassword = async () => {
  if (!certInfo.value?.chc_idx || !newPassword.value.trim()) return;
  saving.value = true;
  try {
    await hopeCertApi.updatePassword(certInfo.value.chc_idx, newPassword.value.trim());
    notify('인증서 비밀번호가 변경되었습니다.');
    closeModal();
    await loadInfo();
  } catch (error: any) {
    const msg = error.response?.data?.message || error.message || '알 수 없는 오류';
    notify(`비밀번호 변경 실패: ${msg}`, 'error');
  } finally {
    saving.value = false;
  }
};

const changeCredentials = async () => {
  if (!certInfo.value?.chc_idx || !hopeId.value.trim() || !hopePw.value.trim()) return;
  saving.value = true;
  try {
    await hopeCertApi.updateCredentials(certInfo.value.chc_idx, hopeId.value.trim(), hopePw.value.trim());
    notify('희망이음 로그인정보가 변경되었습니다.');
    closeModal();
    await loadInfo();
  } catch (error: any) {
    const msg = error.response?.data?.message || error.message || '알 수 없는 오류';
    notify(`로그인정보 변경 실패: ${msg}`, 'error');
  } finally {
    saving.value = false;
  }
};

const deleteCert = async () => {
  if (!certInfo.value?.chc_idx) return;
  saving.value = true;
  try {
    await hopeCertApi.delete(certInfo.value.chc_idx);
    notify('희망이음 인증서가 삭제되었습니다.');
    showDeleteDialog.value = false;
    closeModal();
    await loadInfo();
  } catch (error: any) {
    const msg = error.response?.data?.message || error.message || '알 수 없는 오류';
    notify(`인증서 삭제 실패: ${msg}`, 'error');
  } finally {
    saving.value = false;
  }
};

watch(() => props.mCode, () => loadInfo());
onMounted(() => loadInfo());
</script>
